import { useState, useEffect, useMemo, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { DollarSign, Video, CheckCircle2, Clock, Search, ArrowUp, ArrowDown, ArrowUpDown, Wallet, RotateCcw, Flag, Download, PackageX, Layers } from 'lucide-react';
import { format } from 'date-fns';
import { shortDate } from '@/lib/utils';
import { supabase } from '@/lib/supabase';
import {
  deadlineDaysForPackage, isPastGracePeriod, graceHoursRemaining, graceMsRemaining,
} from '@/lib/deadlines';
import { refundState, partialRefundCents, fundingLabel, usd, isSystemActor, refundActorLabel, type RefundState } from '@/lib/refunds';
import { payoutState, payoutSubmissionCounts, type PayoutState } from '@/lib/payouts';
import { opsBucket, matchesMoneyLens, type OpsBucket, type MoneyLens } from '@/lib/hire-queue';
import { recordHirePayment } from '@/lib/pay-hire';
import { latestTrolleyPayout, TROLLEY_PAYOUT_COLUMNS, type TrolleyPayout } from '@/lib/trolley-payouts';
import { TrolleyPayoutMethodToggle, TrolleyPayoutSendForm, TrolleyPayoutStatusCard, type PayMethod } from '@/components/TrolleyPayout';
import { refundHireToCredit } from '@/lib/workerApi';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { DataPagination, PAGE_SIZE_OPTIONS } from '@/components/DataPagination';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { HireRatingStars } from '@/components/HireRatingStars';
import type { HireFeedback } from '@/lib/hire-rating';
import { HireFlagCountBadge } from '@/components/VideoFlagBadge';
import { ViralRemakeBadge, isViralRemake } from '@/components/ViralRemakeBadge';
import { flagCounts, VIDEO_FLAG_COLUMNS, type VideoFlagFields } from '@/lib/video-flags';
import { SampleReceiptBadge } from '@/components/SampleReceiptBadge';
import { fetchSampleReceipts, type SampleReceipt } from '@/lib/sample-receipt';
import { adminFlagCount, ADMIN_FLAG_COLUMNS, type AdminFlagFields } from '@/lib/admin-video-flag';
import { AdminFlagCountBadge } from '@/components/AdminVideoFlagCell';


type PaymentStatus = 'unpaid' | 'paid';

/** Extends VideoFlagFields because a brand's flag lives on the submission row
 *  itself — so the flagged-video count per hire comes out of the join this
 *  page already does, with no extra query. See lib/video-flags.ts. */
type VideoSubmission = VideoFlagFields & AdminFlagFields & {
  id: string;
  submitted_at: string;
  product_match: boolean | null;
  review_status: 'approved' | 'rejected' | null;
  deleted: boolean;
  // Needed by the partial-refund dialog, which lists the delivered videos the
  // creator is still being paid for.
  tiktok_video_url: string | null;
};

type Hire = {
  id: string;
  campaign_id: string;
  // Set when the hire came from a creator application. The DB trigger that closes
  // a settled hire closes this application too, which is what lets the creator
  // re-apply (see 20260807040000_close_hire_engagement_trigger.sql).
  application_id: string | null;
  brand_id: string;
  creator_id: string;
  status: string;
  package_size: number;
  creator_rate_cents: number;
  brand_rate_cents: number;
  videos_required: number;
  created_at: string | null;
  confirmed_at: string | null;
  completed_at: string | null;
  // Set when ops closed the hire out short of full delivery (refunded); the
  // engagement is over even though `status` can stay 'confirmed' forever.
  // Fetched via the `*` select below — declared here only so the sample-
  // receipt "live" gate (SampleReceiptBadge's `live` prop) can read it.
  settled_at: string | null;
  payment_status: PaymentStatus;
  payment_transaction_id: string | null;
  payment_amount_cents: number | null;
  paid_at: string | null;
  paid_by: string | null;
  payment_notes: string | null;
  deadline_at: string | null;
  deadline_extension_requested_at: string | null;
  deadline_extended_at: string | null;
  deadline_extension_rejected_at: string | null;
  // Which path produced the CURRENT extension — 'creator_request' or
  // 'manual' (brand/admin extended on their own call, no request).
  deadline_extension_source: 'creator_request' | 'manual' | null;
  // Brand-side money: how the hire was funded and what has been refunded to
  // the brand's wallet. Distinct from payment_* above, which is the CREATOR
  // payout side.
  whop_charge_status: string | null;
  credit_applied_cents: number | null;
  card_charged_cents: number | null;
  funding_source: 'credit' | 'card' | 'mixed' | null;
  refunded_credit_cents: number | null;
  refunded_at: string | null;
  refunded_by: string | null;
  refund_reason: string | null;
  roster_creators: {
    name: string | null;
    tiktok_username: string | null;
    email: string | null;
  } | null;
  cr_campaigns: {
    id: string;
    name: string | null;
    product_name: string | null;
    is_viral_remake: boolean | null;
  } | null;
  cr_video_submissions: VideoSubmission[] | null;
  /** Trolley payout attempts sent from this dashboard. A live one blocks Pay
   *  (payoutState) while the hire is still 'unpaid' awaiting Trolley. */
  cr_hire_payouts: TrolleyPayout[] | null;
};

/** 'all' plus one key per OpsBucket — the tabs ARE the partition. */
type FilterTab = 'all' | OpsBucket;
type SortField = 'creator_name' | 'campaign_name' | 'creator_rate_cents' | 'paid_at' | 'created_at' | 'refunded_at' | 'deadline_at';
type SortDir = 'asc' | 'desc';

// ── Tabs ────────────────────────────────────────────────────────────────────
// One axis: what does ops need to do with this hire next. Every hire lands in
// exactly one (see lib/hire-queue.ts), so a row appears once and the counts sum
// to All — neither of which was true when the tabs filtered on delivery status,
// creator payment and brand refunds at the same time.
//
// Colour is urgency, in the order the work should be done: red = a brand is owed
// money it paid and didn't receive, amber = someone is owed a decision or a
// payout, blue = running, nothing to do, emerald = done.
type TabTone = 'neutral' | 'blue' | 'amber' | 'emerald' | 'red';

const TAB_TONES: Record<TabTone, { badge: string; activeText: string; activeBorder: string }> = {
  neutral: { badge: 'bg-slate-200 text-slate-700',     activeText: 'text-foreground',   activeBorder: 'border-foreground' },
  blue:    { badge: 'bg-blue-100 text-blue-800',       activeText: 'text-blue-800',     activeBorder: 'border-blue-500' },
  amber:   { badge: 'bg-amber-100 text-amber-800',     activeText: 'text-amber-800',    activeBorder: 'border-amber-500' },
  emerald: { badge: 'bg-emerald-100 text-emerald-800', activeText: 'text-emerald-800',  activeBorder: 'border-emerald-500' },
  red:     { badge: 'bg-red-100 text-red-800',         activeText: 'text-red-800',      activeBorder: 'border-red-500' },
};

const SORT_FIELDS: ReadonlyArray<SortField> = [
  'creator_name', 'campaign_name', 'creator_rate_cents', 'paid_at', 'created_at', 'refunded_at', 'deadline_at',
];

// ── Filters live in the URL ──────────────────────────────────────────────────
// Not in useState alone. Ops sets up a view — Needs review, brand refunded, sorted
// by refund date, page 3 — clicks a hire, and comes back. Local state is gone by
// then: this is a route change, so the page unmounts and remounts with every filter
// at its default, and the view has to be rebuilt by hand every single time.
//
// The URL is the one place that survives it, because the browser already restores
// it on Back. It also makes a filtered view reloadable and linkable, which local
// state never was — "the four hires I'm looking at" becomes something you can send
// someone.
//
// Read once on mount, mirrored on every change. Writes use replace: true, so
// twenty filter tweaks leave one history entry rather than twenty for Back to
// crawl through.
//
// Defaults are omitted from the query string, so an untouched page stays a clean
// /creator-payments and only what ops actually changed shows up.
// ============================================================================

const DEFAULTS = {
  tab: 'all' as FilterTab,
  lens: 'any' as MoneyLens,
  sort: 'created_at' as SortField,
  dir: 'desc' as SortDir,
  page: 1,
  size: 10,
  flagged: false,
  sampleIssue: false,
  q: '',
  campaign: 'all',
  brand: 'all',
};

/**
 * ?size= snapped to a size the dropdown actually offers.
 *
 * The dropdown can only ever produce 10/25/50/100, but a URL can say anything —
 * ?size=100000 would render every hire in one DOM pass and hang the tab, and a
 * value that isn't an option (?size=37) leaves the Select with no matching item,
 * so "Rows per page" renders blank.
 *
 * Snapped DOWN to the largest offered size that doesn't exceed the request, so an
 * over-large number gives ops the most we support (the 100 cap) rather than
 * dropping them to 10 and looking broken. Below the smallest option, or not a
 * counting number at all, falls back to the default.
 */
function readPageSize(raw: string | null): number {
  const n = Number(raw)
  if (!Number.isInteger(n) || n < 1) return DEFAULTS.size
  const offered = [...PAGE_SIZE_OPTIONS].sort((a, b) => a - b)
  const snapped = offered.filter(size => size <= n).pop()
  return snapped ?? offered[0] ?? DEFAULTS.size
}

/** Every value is validated against the real option list. Query strings get
 *  hand-edited, shared and truncated, and a bad one must read as the default
 *  rather than filtering the table down to nothing with no way to tell why. */
export function readFilters(sp: URLSearchParams) {
  const one = <T,>(key: string, allowed: ReadonlyArray<T>, fallback: T): T => {
    const raw = sp.get(key)
    return allowed.includes(raw as T) ? (raw as T) : fallback
  }
  const int = (key: string, fallback: number, min: number) => {
    const n = Number(sp.get(key))
    return Number.isInteger(n) && n >= min ? n : fallback
  }
  return {
    tab:      one<FilterTab>('tab', TAB_KEYS, DEFAULTS.tab),
    lens:     one<MoneyLens>('lens', LENS_KEYS, DEFAULTS.lens),
    sort:     one<SortField>('sort', SORT_FIELDS, DEFAULTS.sort),
    dir:      one<SortDir>('dir', ['asc', 'desc'], DEFAULTS.dir),
    page:     int('page', DEFAULTS.page, 1),
    size:     readPageSize(sp.get('size')),
    flagged:  sp.get('flagged') === '1',
    sampleIssue: sp.get('sampleIssue') === '1',
    q:        sp.get('q') ?? DEFAULTS.q,
    campaign: sp.get('campaign') || DEFAULTS.campaign,
    brand:    sp.get('brand') || DEFAULTS.brand,
  }
}

/** The inverse. Anything at its default is left out entirely. */
export function writeFilters(f: ReturnType<typeof readFilters>): URLSearchParams {
  const sp = new URLSearchParams()
  if (f.tab      !== DEFAULTS.tab)      sp.set('tab', f.tab)
  if (f.lens     !== DEFAULTS.lens)     sp.set('lens', f.lens)
  if (f.sort     !== DEFAULTS.sort)     sp.set('sort', f.sort)
  if (f.dir      !== DEFAULTS.dir)      sp.set('dir', f.dir)
  if (f.page     !== DEFAULTS.page)     sp.set('page', String(f.page))
  if (f.size     !== DEFAULTS.size)     sp.set('size', String(f.size))
  if (f.flagged)                        sp.set('flagged', '1')
  if (f.sampleIssue)                    sp.set('sampleIssue', '1')
  if (f.q        !== DEFAULTS.q)        sp.set('q', f.q)
  if (f.campaign !== DEFAULTS.campaign) sp.set('campaign', f.campaign)
  if (f.brand    !== DEFAULTS.brand)    sp.set('brand', f.brand)
  return sp
}

const TABS: ReadonlyArray<{ key: FilterTab; label: string; tone: TabTone }> = [
  { key: 'all',          label: 'All',          tone: 'neutral' },
  { key: 'needs_refund', label: 'Needs refund', tone: 'red'     },
  { key: 'needs_payout', label: 'Needs payout', tone: 'amber'   },
  { key: 'needs_review', label: 'Needs review', tone: 'amber'   },
  { key: 'delivering',   label: 'Delivering',   tone: 'blue'    },
  { key: 'settled',      label: 'Settled',      tone: 'emerald' },
];

const TAB_KEYS = TABS.map(t => t.key);

/**
 * Tab tooltips: the action, then one short sentence.
 *
 * Kept deliberately plain. The first version explained the rule accurately — grace
 * windows, pro-rata entitlements, "a partial payout unlocks once the brand is
 * whole" — and was too dense to read at a glance, which is the only moment a
 * tooltip gets. Short words, one idea per sentence. The precise rules live in
 * lib/hire-queue.ts for anyone who needs them; a hover does not.
 */
const TAB_HINTS: Record<FilterTab, { action: string; detail: string }> = {
  all: {
    action: 'All hires',
    detail: 'Each hire is in one other tab only. That is why the counts add up.',
  },
  needs_refund: {
    action: 'Refund the brand',
    detail: 'Videos are missing and time is up. Refund the brand first, then pay the creator.',
  },
  needs_payout: {
    action: 'Pay the creator',
    detail: 'The creator sent videos and has not been paid yet.',
  },
  needs_review: {
    action: 'Your decision needed',
    detail: 'Every video is in and needs approving, or an extension needs an answer. A payment is waiting on you.',
  },
  delivering: {
    action: 'Nothing to do yet',
    detail: 'The creator still has time to send more videos. Any already in can be reviewed here whenever you like.',
  },
  settled: {
    action: 'Finished',
    detail: 'Nobody is owed anything. Some older ones have videos nobody reviewed — that is fine, the money is closed.',
  },
};

/** Empty state per tab. An empty queue is good news and should read that way. */
const TAB_EMPTY: Record<FilterTab, string> = {
  all:          'No hires found.',
  needs_refund: 'No refunds owed — every overdue hire has been settled.',
  needs_payout: 'No payouts owed.',
  needs_review: 'Nothing waiting on a decision.',  // every finished delivery is reviewed and every extension answered
  delivering:   'No hires currently delivering.',
  settled:      'No settled hires yet.',
};

/** The axis the tabs no longer carry, because it overlaps all of them. */
const MONEY_LENSES: ReadonlyArray<{ key: MoneyLens; label: string }> = [
  { key: 'any',                  label: 'Any money state' },
  { key: 'creator_unpaid',       label: 'Creator: unpaid' },
  { key: 'creator_partial',      label: 'Creator: partly paid' },
  { key: 'creator_paid',         label: 'Creator: paid in full' },
  { key: 'brand_refunded',       label: 'Brand: refunded (any)' },
  { key: 'brand_partial_refund', label: 'Brand: partly refunded' },
  { key: 'brand_full_refund',    label: 'Brand: fully refunded' },
];

const LENS_KEYS = MONEY_LENSES.map(l => l.key);

function videosSubmitted(h: Hire) {
  // A video an admin rejected, or one since removed from TikTok, doesn't
  // count toward the requirement — that slot is open again for the creator
  // to submit a replacement, and it can't earn payout either way.
  return payoutSubmissionCounts(h.cr_video_submissions ?? []).delivered;
}

function videosPending(h: Hire) {
  // The slice of the counting videos NOT yet approved. Every video needs an
  // explicit admin decision before a payout, so any pending > 0 blocks Pay.
  return payoutSubmissionCounts(h.cr_video_submissions ?? []).pending;
}

function videosRequired(h: Hire) {
  return h.videos_required ?? h.package_size;
}

function videosMatched(h: Hire) {
  // Rejected/deleted videos are already excluded from eligibility — exclude
  // them here too so they don't keep showing up as "unmatched" / blocking payment.
  const subs = (h.cr_video_submissions ?? []).filter(v => v.review_status !== 'rejected' && !v.deleted);
  const matched = subs.filter(v => v.product_match === true).length;
  return { matched, unmatched: subs.length - matched, total: subs.length };
}

function videosDeleted(h: Hire) {
  // Videos TikTok no longer serves. Every other number on this row excludes
  // them — delivered, matched, the payout gate and the refund math all treat a
  // removed video as a freed slot. Surfaced anyway because a deletion is what
  // silently sends a completed hire back to 'confirmed', and without it on the
  // row that reopening looks like it came from nowhere.
  return (h.cr_video_submissions ?? []).filter(v => v.deleted).length;
}

function PaymentBadge({ status }: { status: PaymentStatus }) {
  if (status === 'paid') {
    return <Badge className="bg-green-100 text-green-800 border border-green-200">Paid</Badge>;
  }
  return <Badge className="bg-yellow-100 text-yellow-800 border border-yellow-200">Unpaid</Badge>;
}

/**
 * A payout that settled for less than the hire's creator rate — the creator was
 * paid for the videos that arrived, not the package. Without this, a $60 partial
 * payout on a $100 hire renders as a plain "Paid" and reads as fully settled, so
 * nobody would ever notice the $40 that was deliberately withheld.
 */
function PartialPayBadge({ hire }: { hire: Hire }) {
  if ((hire.payment_status ?? 'unpaid') !== 'paid') return null;
  const paid = hire.payment_amount_cents ?? 0;
  const rate = hire.creator_rate_cents ?? 0;
  if (paid <= 0 || rate <= 0 || paid >= rate) return null;
  return (
    <Badge className="bg-amber-100 text-amber-800 border border-amber-200 text-[10px] px-1.5 py-0 whitespace-nowrap">
      Partial {usd(paid)} of {usd(rate)}
    </Badge>
  );
}

/** Brand was credited back for this hire. `full` distinguishes "the whole
 *  charge came back" from a part-refund where the creator still delivered some. */
function RefundBadge({ hire }: { hire: Hire }) {
  const refunded = hire.refunded_credit_cents ?? 0;
  if (refunded <= 0) return null;
  const collected = (hire.credit_applied_cents ?? 0) + (hire.card_charged_cents ?? 0);
  const full = collected > 0 && refunded >= collected;
  // "auto" marks a refund no person decided — the deadline sweep credited it.
  // Visible in the table, not just on the hire page, because a row of refunds
  // ops does not remember making is the moment they need to know which were
  // theirs. isSystemActor reads the 'system:' prefix automated callers pass as
  // p_created_by; the same test as SQL's refunded_by LIKE 'system:%'.
  const auto = isSystemActor(hire.refunded_by);
  return (
    <Badge
      className="bg-violet-100 text-violet-800 border border-violet-200 text-[10px] px-1.5 py-0 whitespace-nowrap"
      title={auto ? refundActorLabel(hire.refunded_by) ?? undefined : undefined}
    >
      {full ? 'Refunded' : 'Part refund'} {usd(refunded)}{auto ? ' · auto' : ''}
    </Badge>
  );
}

/**
 * `confirmed` reads as three sub-states depending on the deadline: on-time
 * (Confirmed), inside the 48h post-deadline grace window where the creator
 * still gets to submit or request an extension (Late), or past that grace
 * window with the requirement still short (Pending Refund — mirrors
 * Brand-Dashboard's HiredCreators.tsx StatusPill).
 *
 * The deadline sub-states are suppressed once ANY refund has landed on the hire:
 * "Pending Refund" beside the RefundBadge's "Refunded $47.30" in the next column
 * would tell ops a refund is still owed on money they already sent back. A
 * refunded hire falls through to its plain lifecycle status and lets RefundBadge
 * carry the money story — same split of responsibilities as the brand side, where
 * the status pill stays "Active" on a part-refunded hire.
 */
function HireStatusBadge({ hire, delivered }: { hire: Hire; delivered: number }) {
  const refunded = hire.refunded_credit_cents ?? 0;
  if (hire.status === 'confirmed' && hire.deadline_at && refunded === 0 && delivered < videosRequired(hire)) {
    if (isPastGracePeriod(hire.deadline_at)) {
      return <Badge className="bg-red-100 text-red-800 border border-red-200">Pending Refund</Badge>;
    }
    if (new Date(hire.deadline_at).getTime() < Date.now()) {
      return <Badge className="bg-orange-100 text-orange-800 border border-orange-200">Late</Badge>;
    }
  }
  if (hire.status === 'confirmed')         return <Badge className="bg-blue-100 text-blue-800 border border-blue-200">Confirmed</Badge>;
  if (hire.status === 'completed')         return <Badge className="bg-emerald-100 text-emerald-800 border border-emerald-200">Completed</Badge>;
  if (hire.status === 'partial_completed') return <Badge className="bg-amber-100 text-amber-800 border border-amber-200">Partial Completed</Badge>;
  if (hire.status === 'failed')            return <Badge className="bg-orange-100 text-orange-800 border border-orange-200">Failed</Badge>;
  return <Badge variant="outline">{hire.status}</Badge>;
}

function DeadlineExtBadge({ hire }: { hire: Hire }) {
  if (hire.deadline_extension_rejected_at) {
    return <Badge className="bg-red-100 text-red-700 border border-red-200 text-[10px] px-1.5 py-0">Ext. Rejected</Badge>;
  }
  if (hire.deadline_extended_at) {
    const label = hire.deadline_extension_source === 'manual' ? 'Ext. Granted (manual)' : 'Ext. Granted';
    return <Badge className="bg-blue-100 text-blue-700 border border-blue-200 text-[10px] px-1.5 py-0">{label}</Badge>;
  }
  if (hire.deadline_extension_requested_at) {
    return <Badge className="bg-amber-100 text-amber-800 border border-amber-200 text-[10px] px-1.5 py-0">Ext. Pending</Badge>;
  }
  return null;
}

/** "3h 20m" / "45m" — mirrors Brand-Dashboard's HiredCreators.tsx, never shows 0m. */
function formatRemaining(ms: number): string {
  const totalMinutes = Math.max(1, Math.ceil(ms / 60000));
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  if (hours === 0) return `${minutes}m`;
  if (minutes === 0) return `${hours}h`;
  return `${hours}h ${minutes}m`;
}

/**
 * Deadline date + time remaining, with the base window the package earns
 * underneath. Delivery windows scale with package size (14d for 5 videos up to
 * 35d for 30), so the window line tells ops whether a tight-looking deadline is
 * by design or a bad row. Once the deadline itself has passed and the creator
 * still hasn't delivered, a third line tracks the 48h post-deadline grace
 * window — how long until the hire flips to Pending Refund.
 */
function DeadlineCell({ hire, delivered, required }: { hire: Hire; delivered: number; required: number }) {
  const windowDays = deadlineDaysForPackage(hire.package_size);
  if (!hire.deadline_at) {
    return (
      <div className="text-xs text-muted-foreground whitespace-nowrap">
        — <span className="text-muted-foreground/70">({windowDays}d window)</span>
      </div>
    );
  }
  const daysLeft = Math.ceil((new Date(hire.deadline_at).getTime() - Date.now()) / 86400000);
  const isTerminal = hire.status === 'completed' || hire.status === 'failed' || hire.status === 'cancelled';
  const tone = isTerminal
    ? 'text-muted-foreground'
    : daysLeft < 0 ? 'text-red-600' : daysLeft <= 3 ? 'text-amber-700' : 'text-foreground';
  const graceMs = graceMsRemaining(hire.deadline_at);
  const showGrace = !isTerminal && daysLeft < 0 && delivered < required && graceMs != null;
  return (
    <div className="whitespace-nowrap">
      <div className={`text-sm font-medium ${tone}`}>
        {format(new Date(hire.deadline_at), 'MMM d, yyyy')}
      </div>
      <div className="text-xs text-muted-foreground">
        {isTerminal
          ? `${windowDays}d window`
          : daysLeft < 0
            ? `${Math.abs(daysLeft)}d overdue · ${windowDays}d window`
            : `${daysLeft}d left · ${windowDays}d window`}
      </div>
      {showGrace && (
        <div className={`text-xs font-medium ${graceMs > 0 ? 'text-orange-600' : 'text-red-600'}`}>
          {graceMs > 0 ? `${formatRemaining(graceMs)} left in grace window` : 'Grace window closed'}
        </div>
      )}
    </div>
  );
}

/** Sortable column header. Six of these differed only in field and label. */
function SortHead({ field, label, align, sortField, sortDir, onSort }: {
  field: SortField;
  label: string;
  align?: 'right';
  sortField: SortField;
  sortDir: SortDir;
  onSort: (f: SortField) => void;
}) {
  return (
    <TableHead
      className={`cursor-pointer select-none whitespace-nowrap ${align === 'right' ? 'text-right' : ''}`}
      onClick={() => onSort(field)}
    >
      <span className={`inline-flex items-center gap-1 ${align === 'right' ? 'justify-end' : ''}`}>
        {label}
        {sortField === field
          ? sortDir === 'asc' ? <ArrowUp className="h-3.5 w-3.5" /> : <ArrowDown className="h-3.5 w-3.5" />
          : <ArrowUpDown className="h-3.5 w-3.5 text-muted-foreground/50" />}
      </span>
    </TableHead>
  );
}

function StatCard({ icon: Icon, label, value, sub }: {
  icon: React.ElementType;
  label: string;
  value: React.ReactNode;
  sub?: string;
}) {
  return (
    <div className="rounded-lg border bg-card p-4 space-y-2">
      <div className="flex items-center gap-1.5">
        <Icon className="h-3.5 w-3.5 text-muted-foreground" />
        <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">{label}</p>
      </div>
      <p className="text-2xl font-bold">{value}</p>
      {sub && <p className="text-xs text-muted-foreground">{sub}</p>}
    </div>
  );
}

export default function CreatorPayments() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  // Read ONCE, on mount. Coming back from a hire is a remount, and this is where
  // the filters ops set up before clicking away come back from. After mount the
  // state below owns the values and the effect further down mirrors them out —
  // reading continuously instead would fight every setState.
  const [searchParams, setSearchParams] = useSearchParams();
  const initial = useRef(readFilters(searchParams)).current;

  const [filterTab, setFilterTab]         = useState<FilterTab>(initial.tab);
  const [searchInput, setSearchInput]     = useState(initial.q);
  // Seeded from the same value rather than '' so a restored search filters on the
  // first render instead of showing every hire for the debounce's 300ms.
  const [search, setSearch]               = useState(initial.q);
  const [campaignFilter, setCampaignFilter] = useState<string>(initial.campaign);
  const [brandFilter, setBrandFilter]     = useState<string>(initial.brand);
  const [moneyLens, setMoneyLens]         = useState<MoneyLens>(initial.lens);
  /** "Only hires with a video the brand flagged and we haven't answered." */
  const [flaggedOnly, setFlaggedOnly]     = useState(initial.flagged);
  /** "Only hires where the creator reported a sample problem still open." */
  const [sampleIssueOnly, setSampleIssueOnly] = useState(initial.sampleIssue);
  const [sortField, setSortField]         = useState<SortField>(initial.sort);
  const [sortDir, setSortDir]             = useState<SortDir>(initial.dir);
  const [page, setPage]                   = useState(initial.page);
  const [pageSize, setPageSize]           = useState(initial.size);
  const [payingHire, setPayingHire]       = useState<Hire | null>(null);
  const [refundingHire, setRefundingHire] = useState<Hire | null>(null);

  const [txId, setTxId]     = useState('');
  const [amount, setAmount] = useState('');
  const [notes, setNotes]   = useState('');
  // Trolley is the default: it's the path that doesn't depend on someone
  // copying an id between two tabs correctly.
  const [payMethod, setPayMethod] = useState<PayMethod>('trolley');

  // Refund is video-driven, not amount-driven: ops decides how many of the
  // undelivered videos to give back, and the amount follows from the per-video
  // price the brand actually paid. A free-form dollar box invited typos on the
  // one action that creates money.
  const [refundMode, setRefundMode]         = useState<'full' | 'partial'>('full');
  const [refundVideos, setRefundVideos]     = useState(0);
  const [refundReason, setRefundReason]     = useState('');
  const [refundKey, setRefundKey]           = useState('');

  // Debounce search. Skips its own first run: `search` is already seeded from the
  // URL, so the only thing the mount pass would do is setPage(1) — throwing away
  // the page number we just restored.
  const searchSettled = useRef(false);
  useEffect(() => {
    if (!searchSettled.current) { searchSettled.current = true; return; }
    const t = setTimeout(() => { setSearch(searchInput); setPage(1); }, 300);
    return () => clearTimeout(t);
  }, [searchInput]);

  // ── Mirror the filter set back into the URL ────────────────────────────────
  // replace: true, so a session of tweaking filters leaves ONE history entry and
  // Back returns to wherever ops came from, not through every intermediate filter
  // state. Skipped when the string is unchanged, so this can't loop on itself or
  // add a no-op navigation on mount.
  useEffect(() => {
    const next = writeFilters({
      tab: filterTab, lens: moneyLens, sort: sortField, dir: sortDir,
      page, size: pageSize, flagged: flaggedOnly, sampleIssue: sampleIssueOnly, q: search,
      campaign: campaignFilter, brand: brandFilter,
    }).toString();
    if (next !== searchParams.toString()) setSearchParams(next, { replace: true });
  }, [filterTab, moneyLens, sortField, sortDir, page, pageSize, flaggedOnly, sampleIssueOnly, search,
      campaignFilter, brandFilter, searchParams, setSearchParams]);

  function handleTabChange(tab: FilterTab) {
    setFilterTab(tab);
    setPage(1);
  }

  function handleCampaignChange(value: string) {
    setCampaignFilter(value);
    setPage(1);
  }

  function handleBrandChange(value: string) {
    setBrandFilter(value);
    setPage(1);
  }

  function handleMoneyLensChange(value: MoneyLens) {
    setMoneyLens(value);
    // A refund lens reads newest-refund-first: it's being used as a log ("what did
    // we refund lately?"), and hire date would bury this morning's refund under
    // hires from weeks ago.
    if (value.startsWith('brand_')) {
      setSortField('refunded_at');
      setSortDir('desc');
    } else if (sortField === 'refunded_at') {
      setSortField('created_at');
      setSortDir('desc');
    }
    setPage(1);
  }

  function handleFlaggedOnlyChange(next: boolean) {
    setFlaggedOnly(next);
    setPage(1);
  }

  function handleSampleIssueOnlyChange(next: boolean) {
    setSampleIssueOnly(next);
    setPage(1);
  }

  function handleSort(field: SortField) {
    if (sortField === field) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortField(field); setSortDir('asc'); }
    setPage(1);
  }

  // ── One fetch, every hire ──────────────────────────────────────────────────
  // Replaces the old pair of queries (a paginated page query + a separate stats
  // query) with a single source of truth. The tabs now filter on a DERIVED bucket
  // that depends on the delivered-video count, which lives in cr_video_submissions
  // — nothing the database can filter or count for us without a view. Splitting
  // "which rows" from "how many" across two queries was also the reason the counts
  // never reconciled: the stats query ignored the campaign and search filters
  // entirely, so the badges described a different set of hires than the table did.
  //
  // Fine at this volume (~150 hires); it degrades in the low thousands, at which
  // point the fix is a cr_hires_ops view exposing delivered_videos + ops_bucket so
  // this can go back to filtering and paginating server-side.
  //
  // Campaign and brand ARE plain columns on cr_hires though, so those two don't
  // need to wait on that view — pushed down to the query itself (queryKey included,
  // so switching either refetches) instead of fetched-then-thrown-away client-side.
  // That keeps the batched-fetch cap meaningful at scale (a brand/campaign filter
  // now shrinks what crosses the wire instead of the cap still applying to
  // everything and the filter only trimming what's already in memory).
  const { data: allHires = [], isLoading } = useQuery<Hire[]>({
    queryKey: ['creator-payments-all', campaignFilter, brandFilter],
    queryFn: async () => {
      const SELECT = `*, deadline_extension_requested_at, deadline_extended_at, deadline_extension_rejected_at, roster_creators(name, tiktok_username, email), cr_campaigns(id, name, product_name, is_viral_remake), cr_video_submissions(id, submitted_at, product_match, review_status, deleted, tiktok_video_url, ${VIDEO_FLAG_COLUMNS}, ${ADMIN_FLAG_COLUMNS}), cr_hire_payouts(${TROLLEY_PAYOUT_COLUMNS})`;
      // Batched rather than one unbounded select: PostgREST caps a response at
      // max-rows (1000 by default), and it does so SILENTLY. An unbounded select
      // would look like it worked and quietly drop every hire past the cap —
      // wrong counts, missing work, no error. Loop until a short page comes back.
      const BATCH = 1000;
      const MAX_BATCHES = 25; // 25k hires; a backstop against an endless loop
      const rows: Hire[] = [];
      for (let i = 0; i < MAX_BATCHES; i++) {
        let query = supabase
          .from('cr_hires')
          .select(SELECT)
          .in('status', ['confirmed', 'completed', 'failed', 'partial_completed'])
          .order('created_at', { ascending: false })
          .range(i * BATCH, (i + 1) * BATCH - 1);
        if (campaignFilter !== 'all') query = query.eq('campaign_id', campaignFilter);
        if (brandFilter !== 'all') query = query.eq('brand_id', brandFilter);
        const { data, error } = await query;
        if (error) {
          if (error.code === '42P01') return rows; // table absent (fresh env)
          throw error;
        }
        const batch = (data ?? []) as Hire[];
        rows.push(...batch);
        if (batch.length < BATCH) return rows;
      }
      return rows;
    },
    staleTime: 30_000,
  });

  // ── Brand rating, batched ─────────────────────────────────────────────────
  // One extra query for every hire currently in view, instead of one per row —
  // same shape as brandNameById below. Keyed on the actual hire ids (not just
  // the campaign/brand filters) so a hire that's brand-new in `allHires` always
  // gets its feedback fetched, even if the filters themselves didn't change.
  const hireIds = useMemo(() => allHires.map(h => h.id), [allHires]);
  const { data: feedbackRows = [] } = useQuery<HireFeedback[]>({
    queryKey: ['creator-payments-feedback', hireIds.join(',')],
    queryFn: async () => {
      if (hireIds.length === 0) return [];
      // Chunked the same way the hires query batches its OUTPUT past 1000 rows
      // — here it's the .in() list that could get long at scale, so batch the
      // INPUT instead. 500 ids/request is comfortably under any URL-length
      // concern for a Supabase REST call.
      const BATCH = 500;
      const rows: HireFeedback[] = [];
      for (let i = 0; i < hireIds.length; i += BATCH) {
        const chunk = hireIds.slice(i, i + BATCH);
        const { data, error } = await supabase
          .from('cr_hire_feedback')
          .select('hire_id, rating, notes, submitted_by_email, created_at')
          .in('hire_id', chunk);
        if (error) {
          if (error.code === '42P01') return rows; // table absent (fresh env)
          throw error;
        }
        rows.push(...((data ?? []) as HireFeedback[]));
      }
      return rows;
    },
    enabled: hireIds.length > 0,
    staleTime: 30_000,
  });
  const feedbackByHireId = useMemo(() => {
    const m = new Map<string, HireFeedback>();
    for (const f of feedbackRows) m.set(f.hire_id, f);
    return m;
  }, [feedbackRows]);

  // ── Creator's own "did your sample arrive?" answer, batched ─────────────
  // Same shape as the feedback query above — one extra request for every hire
  // in view rather than per row. Fail-soft (resolves {} on error, e.g. the
  // migration not applied yet — see fetchSampleReceipts), so a lookup failure
  // here can only blank the Sample column, never the rest of the table.
  const { data: receiptsByHireId = {} } = useQuery<Record<string, SampleReceipt>>({
    queryKey: ['creator-payments-sample-receipts', hireIds.join(',')],
    queryFn: () => fetchSampleReceipts(hireIds),
    enabled: hireIds.length > 0,
    staleTime: 30_000,
  });

  // ── Derivation ─────────────────────────────────────────────────────────────
  // Each hire is decorated once with its delivered count and its single bucket, so
  // the row, the action cell and the tab count all read the same numbers.
  const decorated = useMemo(() => allHires.map(h => {
    const delivered = videosSubmitted(h);
    return {
      hire: h,
      delivered,
      // Brand flags on this hire's videos. Informational only — no bucket and
      // no payout gate reads this; see lib/video-flags.ts for why. Videos
      // removed from TikTok are excluded, exactly like the delivered count
      // above: a flag on a video that no longer exists isn't ops' work.
      flags: flagCounts((h.cr_video_submissions ?? []).filter(v => !v.deleted)),
      // A BMC admin's own hold — separate from the brand's flag above (neither
      // implies the other). Also informational: no bucket/payout gate reads it,
      // only the auto-approve/auto-refund crons (worker-side) do.
      adminFlagged: adminFlagCount((h.cr_video_submissions ?? []).filter(v => !v.deleted)),
      required: videosRequired(h),
      match: videosMatched(h),
      // Context only, same as the flags above — no bucket, payout gate or
      // refund calculation reads this. See videosDeleted().
      deletedVideos: videosDeleted(h),
      refund: refundState(h, delivered),
      payout: payoutState(h, delivered, videosPending(h)),
      bucket: opsBucket(h, delivered, videosPending(h)),
    };
  }), [allHires]);

  // Everything EXCEPT the bucket tab. The tab counts are computed over this set,
  // which is what makes them add up to the All count and follow the money and
  // search filters instead of describing some other population. Campaign and
  // brand are no longer filtered here — `allHires` is already scoped to them at
  // the DB query above, so `decorated` only ever contains matching rows.
  const scoped = useMemo(() => {
    const q = search.trim().toLowerCase();
    return decorated.filter(({ hire, flags, adminFlagged }) => {
      // Another overlapping lens, like the money one: a hire with a flagged
      // video can be in any bucket, so it filters ACROSS the tabs rather than
      // being one of them. Unanswered flags only — a hire whose flags we have
      // already dealt with isn't work.
      if (flaggedOnly && flags.active === 0 && adminFlagged === 0) return false;
      // Same kind of cross-tab lens as Flagged: a reported sample problem can
      // sit on a hire in any bucket (still delivering, awaiting payout, even
      // settled), so it filters across the tabs instead of being one.
      if (sampleIssueOnly && receiptsByHireId[hire.id]?.sample_receipt_status !== 'problem') return false;
      if (!matchesMoneyLens(hire, moneyLens)) return false;
      if (!q) return true;
      const haystack = [
        hire.roster_creators?.name,
        hire.roster_creators?.tiktok_username,
        hire.roster_creators?.email,
        hire.cr_campaigns?.name,
        hire.cr_campaigns?.product_name,
        hire.payment_transaction_id,
      ];
      return haystack.some(v => v?.toLowerCase().includes(q));
    });
  }, [decorated, moneyLens, search, flaggedOnly, sampleIssueOnly, receiptsByHireId]);

  const tabCounts = useMemo(() => {
    const counts: Record<FilterTab, number> = {
      all: scoped.length, needs_refund: 0, needs_payout: 0, needs_review: 0, delivering: 0, settled: 0,
    };
    for (const row of scoped) counts[row.bucket]++;
    return counts;
  }, [scoped]);

  const moneyTotals = useMemo(() => ({
    // Owed = what the creators in view have actually EARNED and not been paid, not
    // the sum of their full rates: a hire that delivered 2 of 10 owes 2 videos'
    // worth, and counting the package overstated the liability by the part nobody
    // will ever pay.
    owed: scoped
      .filter(r => (r.hire.payment_status ?? 'unpaid') === 'unpaid')
      .reduce((s, r) => s + r.payout.earnedCents, 0),
    paid: scoped
      .filter(r => r.hire.payment_status === 'paid')
      .reduce((s, r) => s + (r.hire.payment_amount_cents ?? r.hire.creator_rate_cents ?? 0), 0),
    refunded: scoped.reduce((s, r) => s + (r.hire.refunded_credit_cents ?? 0), 0),
  }), [scoped]);

  const sortedRows = useMemo(() => {
    const dir = sortDir === 'asc' ? 1 : -1;
    const time = (v: string | null | undefined) => (v ? new Date(v).getTime() : 0);
    const rows = scoped.filter(r => filterTab === 'all' || r.bucket === filterTab);
    return [...rows].sort((a, b) => {
      switch (sortField) {
        case 'creator_name':  return dir * (a.hire.roster_creators?.name ?? '').localeCompare(b.hire.roster_creators?.name ?? '');
        case 'campaign_name': return dir * (a.hire.cr_campaigns?.name ?? '').localeCompare(b.hire.cr_campaigns?.name ?? '');
        case 'creator_rate_cents': return dir * ((a.hire.creator_rate_cents ?? 0) - (b.hire.creator_rate_cents ?? 0));
        case 'paid_at':       return dir * (time(a.hire.paid_at) - time(b.hire.paid_at));
        case 'refunded_at':   return dir * (time(a.hire.refunded_at) - time(b.hire.refunded_at));
        case 'deadline_at': {
          // Hires with no deadline (not confirmed yet) go last in BOTH directions,
          // not first on desc — they have nothing to compare.
          const ad = a.hire.deadline_at, bd = b.hire.deadline_at;
          if (!ad || !bd) return ad ? -1 : bd ? 1 : 0;
          return dir * (time(ad) - time(bd));
        }
        case 'created_at':
        default:              return dir * (time(a.hire.created_at) - time(b.hire.created_at));
      }
    });
  }, [scoped, filterTab, sortField, sortDir]);

  // ── CSV export ─────────────────────────────────────────────────────────────
  // Exports `sortedRows`, not `pageRows`: the whole filtered/sorted result (tab,
  // brand, campaign, money lens, search), independent of page and page size.
  function exportCsv() {
    if (sortedRows.length === 0) return;
    const dollars = (cents: number | null | undefined) => ((cents ?? 0) / 100).toFixed(2);
    const iso = (v: string | null | undefined) => (v ? new Date(v).toISOString() : '');
    const cell = (v: unknown) => {
      let s = v == null ? '' : String(v);
      // Stop spreadsheet apps from evaluating user-supplied text as a formula.
      if (/^[=+\-@\t\r]/.test(s)) s = `'${s}`;
      return `"${s.replace(/"/g, '""')}"`;
    };
    const headers = [
      'Creator', 'TikTok Username', 'Email', 'Brand', 'Campaign', 'Product',
      'Ops Status', 'Hire Status', 'Deadline', 'Videos Delivered', 'Videos Required',
      'Creator Rate (USD)', 'Earned (USD)', 'Payment Status', 'Paid Amount (USD)',
      'Paid At', 'Transaction ID', 'Paid By', 'Refunded to Brand (USD)', 'Refunded At',
      'Hired At',
    ];
    const lines = sortedRows.map(({ hire: h, delivered, required, bucket, payout }) => [
      h.roster_creators?.name,
      h.roster_creators?.tiktok_username ? `@${h.roster_creators.tiktok_username}` : '',
      h.roster_creators?.email,
      brandNameById.get(h.brand_id) ?? h.brand_id,
      h.cr_campaigns?.name ?? h.campaign_id,
      h.cr_campaigns?.product_name,
      TABS.find(t => t.key === bucket)?.label ?? bucket,
      h.status,
      iso(h.deadline_at),
      delivered,
      required,
      dollars(h.creator_rate_cents),
      dollars(payout.earnedCents),
      h.payment_status ?? 'unpaid',
      h.payment_status === 'paid' ? dollars(h.payment_amount_cents ?? h.creator_rate_cents) : '',
      iso(h.paid_at),
      h.payment_transaction_id,
      h.paid_by,
      dollars(h.refunded_credit_cents),
      iso(h.refunded_at),
      iso(h.created_at),
    ].map(cell).join(','));
    const csv = '﻿' + [headers.map(cell).join(','), ...lines].join('\n');
    const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv;charset=utf-8;' }));
    const link = document.createElement('a');
    link.href = url;
    link.download = `creator-payments-${new Date().toISOString().slice(0, 10)}.csv`;
    link.click();
    URL.revokeObjectURL(url);
    toast.success(`Exported ${sortedRows.length} hire${sortedRows.length === 1 ? '' : 's'}`);
  }

  // recordHirePayment owns the eligibility re-check, the payload and the audit
  // entry — shared with the hire detail page's payout panel so the two can't
  // write a payout differently (see lib/pay-hire.ts).
  const payMutation = useMutation({
    mutationFn: (hire: Hire) => recordHirePayment(hire, {
      transactionId: txId,
      amountCents: Math.round(Number(amount) * 100),
      notes,
      deliveredVideos: videosSubmitted(hire),
    }),
    onSuccess: (res) => {
      toast.success(res.mode === 'partial'
        ? `Partial payout of ${usd(res.amountCents)} recorded for ${res.deliveredVideos} of ${res.requiredVideos} videos`
        : 'Hire marked as paid');
      closeModal();
      queryClient.invalidateQueries({ queryKey: ['creator-payments-all'] });
    },
    onError: (err: any) => toast.error(err.message || 'Failed to record payment'),
  });

  // ── Refund to brand wallet credit ─────────────────────────────────────────
  // The worker + DB own the rules; this just collects the amount/reason and
  // sends one attributable, idempotent request.
  const refundMutation = useMutation({
    mutationFn: async ({ hire, amountCents, reason, key }: { hire: Hire; amountCents: number; reason: string; key: string }) => {
      const { data: { session } } = await supabase.auth.getSession();
      const adminEmail = session?.user?.email ?? '';
      // Refunds create money, so they must be attributable. No email = no refund.
      if (!adminEmail) throw new Error('Could not determine your admin email — sign out and back in, then retry.');
      return refundHireToCredit(hire.id, adminEmail, {
        amount_cents: amountCents,
        reason: reason || undefined,
        idempotency_key: key,
      });
    },
    onSuccess: (res) => {
      if (res.replayed) {
        toast.info('This refund was already applied — no new credit was created.');
      } else {
        toast.success(
          `Refunded ${usd(res.refunded_cents)} to the brand's wallet` +
          (res.fully_refunded ? ' (full refund)' : '')
        );
      }
      closeRefundModal();
      queryClient.invalidateQueries({ queryKey: ['creator-payments-all'] });
    },
    onError: (err: any) => toast.error(err.message || 'Refund failed'),
  });

  function openRefundModal(hire: Hire) {
    const state = refundState(hire, videosSubmitted(hire));
    setRefundingHire(hire);
    // Nothing delivered → full refund is the only sensible option, and the
    // default. Otherwise open on partial with every undelivered video selected,
    // which is the common case: refund the gap, keep paying for what arrived.
    setRefundMode(state.fullOnly ? 'full' : 'partial');
    // Videos still UNREFUNDED — on a second refund the already-settled ones must
    // not be offered again.
    setRefundVideos(state.refundableVideos);
    setRefundReason('');
    // One key per opened dialog: retries of THIS refund collapse into a no-op,
    // while a deliberate second refund later gets its own key.
    setRefundKey(`hire_refund:${hire.id}:${crypto.randomUUID()}`);
  }

  function closeRefundModal() {
    setRefundingHire(null);
    setRefundMode('full');
    setRefundVideos(0);
    setRefundReason('');
    setRefundKey('');
  }

  function openPayModal(hire: Hire) {
    setPayingHire(hire);
    setPayMethod('trolley');
    setTxId('');
    // Seed the EARNED amount, not the hire's full rate: on a partial payout the
    // full rate is the one number that must not be the default — it's both the
    // wrong figure and the plausible-looking one. An amount already recorded on
    // the row still wins, so a re-open shows what was entered before.
    const earned = payoutState(hire, videosSubmitted(hire), videosPending(hire)).earnedCents;
    setAmount(String((hire.payment_amount_cents || earned) / 100));
    setNotes('');
  }

  function closeModal() {
    setPayingHire(null);
    setTxId('');
    setAmount('');
    setNotes('');
  }

  const { data: campaigns = [] } = useQuery<{ id: string; name: string; brand_id: string }[]>({
    queryKey: ['creator-payments-campaigns'],
    queryFn: async () => {
      // Drafts aren't hireable yet, so they shouldn't show up as something to
      // filter payments by. A hire's own cr_campaigns row (joined in the main
      // query above) isn't touched by this — only the filter dropdown's list.
      // brand_id is selected here (not just for display) so the Brand filter
      // below can derive "brands with an actual (non-draft) job" from this same
      // fetch instead of a second round trip.
      const { data, error } = await supabase
        .from('cr_campaigns')
        .select('id, name, brand_id')
        .neq('status', 'draft')
        .order('name', { ascending: true });
      if (error) throw error;
      return (data ?? []).map((c: any) => ({ id: c.id, name: c.name ?? c.id, brand_id: c.brand_id }));
    },
    staleTime: 60_000,
  });

  const { data: brands = [] } = useQuery<{ id: string; brand_name: string }[]>({
    queryKey: ['creator-payments-brands'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('brand_configs')
        .select('id, brand_name')
        .order('brand_name', { ascending: true });
      if (error) throw error;
      return (data ?? []).map((b: any) => ({ id: b.id, brand_name: b.brand_name ?? b.id }));
    },
    staleTime: 60_000,
  });

  // cr_hires carries brand_id but the row's own SELECT doesn't join brand_configs
  // (only roster_creators + cr_campaigns) — the Brand filter's own lookup already
  // gives us every id → name pair, so reuse it here instead of widening that join.
  // Deliberately built from the FULL `brands` list (not brandOptions below) so a
  // hire's brand name always resolves in the table, even for a brand that
  // wouldn't currently qualify for the filter dropdown.
  const brandNameById = useMemo(() => {
    const m = new Map<string, string>();
    for (const b of brands) m.set(b.id, b.brand_name);
    return m;
  }, [brands]);

  // Brand filter dropdown options — only brands with at least one non-draft
  // campaign (i.e. an actual posted job). A brand that has never posted a job,
  // or whose only campaign(s) are still drafts, doesn't belong in this list.
  // `campaigns` above already excludes drafts, so "has a job" is just "appears
  // as a brand_id there" — no extra query needed.
  const brandOptions = useMemo(() => {
    const brandIdsWithJobs = new Set(campaigns.map(c => c.brand_id).filter(Boolean));
    return brands.filter(b => brandIdsWithJobs.has(b.id));
  }, [brands, campaigns]);

  const totalItems = sortedRows.length;
  const totalPages = Math.max(1, Math.ceil(totalItems / pageSize));
  const pageRows   = useMemo(
    () => sortedRows.slice((page - 1) * pageSize, page * pageSize),
    [sortedRows, page, pageSize],
  );

  // A filter change can leave the current page past the end of a shorter result
  // set — with server-side pagination the query just returned nothing, which read
  // as "no hires" on a tab that had plenty.
  useEffect(() => {
    if (page > totalPages) setPage(1);
  }, [page, totalPages]);

  const payingHireMatch = payingHire ? videosMatched(payingHire) : { matched: 0, unmatched: 0, total: 0 };
  const payingPayout: PayoutState | null = payingHire
    ? payoutState(payingHire, videosSubmitted(payingHire), videosPending(payingHire))
    : null;
  // Paying more than the creator earned is the one direction that loses money, so
  // it gets a warning (not a block — ops may be settling something off-system).
  const payAmountCents = Math.round(Number(amount) * 100);
  const payOverEarned = !!payingPayout && payAmountCents > payingPayout.earnedCents;
  // The red "payout blocked" box is a full-payout concept. On a partial payout the
  // unmatched videos are precisely what ops is knowingly pricing, and the amber
  // receipt already carries that count where it's actionable — showing this box
  // there would put "payout blocked" next to a working Pay button.
  const showMatchBlock = payingHireMatch.unmatched > 0 && payingPayout?.mode !== 'partial';
  const showMatchOk    = payingHireMatch.total > 0 && payingHireMatch.unmatched === 0;
  const canPay = txId.trim().length > 0 && Number(amount) > 0 && !!payingPayout?.eligible;
  const payingTrolley = payingHire
    ? latestTrolleyPayout((allHires.find(h => h.id === payingHire.id) ?? payingHire).cr_hire_payouts)
    : null;

  const refundingState: RefundState | null = refundingHire
    ? refundState(refundingHire, videosSubmitted(refundingHire))
    : null;
  // Full = everything still refundable (never more — the DB caps it too).
  // Partial = per-video price x videos being refunded.
  const refundAmountCents = !refundingState
    ? 0
    : refundMode === 'full'
      ? refundingState.refundableCents
      : partialRefundCents(refundingState, refundVideos);
  // Videos the creator keeps being paid for under the current selection.
  const videosKeptPaid = refundingState
    ? (refundingHire ? videosRequired(refundingHire) : 0) - (refundMode === 'full' ? (refundingHire ? videosRequired(refundingHire) : 0) : refundVideos)
    : 0;
  const canRefund = !!refundingState
    && refundAmountCents > 0
    && refundAmountCents <= refundingState.refundableCents
    && (refundMode === 'full' || refundVideos > 0);

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold tracking-tight">Creator Payments</h1>
          <p className="text-sm text-muted-foreground mt-1">Track and record payouts for confirmed, completed, and failed hires.</p>
        </div>
        <Button size="sm" variant="outline" onClick={() => navigate('/creator-payments/batches')}>
          <Layers className="h-4 w-4 mr-1" /> Payout batches
        </Button>
      </div>

      {/* Stat cards. Every number on this page — these, the tab counts, the table
          — now describes the SAME set: whatever the campaign, search and money
          filters currently select. Previously the cards and tab badges came from
          their own query that ignored those filters, so picking one campaign left
          totals from all of them on screen and nothing reconciled with anything. */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        <StatCard icon={Clock}        label="Needs Action"
          value={tabCounts.needs_refund + tabCounts.needs_payout + tabCounts.needs_review}
          sub={`${tabCounts.needs_refund} refund · ${tabCounts.needs_payout} payout · ${tabCounts.needs_review} review`} />
        <StatCard icon={DollarSign}   label="Owed to Creators" value={usd(moneyTotals.owed)}
          sub="earned but not paid" />
        <StatCard icon={CheckCircle2} label="Paid Out"         value={usd(moneyTotals.paid)} />
        <StatCard icon={Wallet}       label="Refunded to Brands" value={usd(moneyTotals.refunded)} sub="as wallet credit" />
      </div>

      {/* Table */}
      <div className="rounded-lg border bg-card">
        {/* Tabs + Search */}
        <div className="flex flex-col sm:flex-row sm:items-center gap-2 border-b px-4 pt-1 pb-3">
          {/* One provider for the whole strip rather than one per tab: the open
              delay is shared, so sliding along the tabs reads like one control
              being explored instead of five tooltips each waiting their turn.
              200ms is deliberate — long enough not to fire while the cursor is
              just travelling to a tab, short enough to feel like an answer. */}
          <TooltipProvider delayDuration={200}>
            <div className="flex items-center gap-1 flex-wrap">
              {TABS.map(({ key, label, tone }) => {
                const count = tabCounts[key];
                const active = filterTab === key;
                const t = TAB_TONES[tone];
                const hint = TAB_HINTS[key];
                return (
                  <Tooltip key={key}>
                    <TooltipTrigger asChild>
                      <button
                        type="button"
                        onClick={() => handleTabChange(key)}
                        className={`px-3 py-2.5 text-sm font-medium transition-colors flex items-center gap-1.5 ${
                          active
                            ? `border-b-2 ${t.activeBorder} ${t.activeText}`
                            : 'text-muted-foreground hover:text-foreground'
                        }`}
                      >
                        {label}
                        {/* An empty queue is not news: a red "0" beside Needs
                            refund would pull the eye every day nothing is owed.
                            Colour appears only when the tab has something in it. */}
                        <span className={`text-xs rounded-full px-1.5 py-0.5 tabular-nums ${
                          count > 0 ? t.badge : 'bg-muted text-muted-foreground'
                        }`}>
                          {count}
                        </span>
                      </button>
                    </TooltipTrigger>
                    <TooltipContent side="bottom" className="max-w-[280px]">
                      <p className="font-semibold">{hint.action}</p>
                      <p className="text-xs opacity-90 leading-snug mt-0.5">{hint.detail}</p>
                    </TooltipContent>
                  </Tooltip>
                );
              })}
            </div>
          </TooltipProvider>

          <div className="sm:ml-auto flex items-center gap-2 w-full sm:w-auto flex-wrap justify-end">
            {/* The money axis, as a filter rather than tabs — it OVERLAPS every
                bucket by nature. A partly-refunded hire can be awaiting a payout,
                or settled, or still delivering, and all three are true at once;
                that's exactly what made tabs the wrong control for it. Combines
                with any tab, so "refunded hires still needing a payout" is one
                selection instead of an impossible one. */}
            <Select value={moneyLens} onValueChange={handleMoneyLensChange}>
              <SelectTrigger
                title="Filter by money. Works together with any tab."
                className={`h-8 text-sm w-full sm:w-[190px] ${
                  moneyLens !== 'any' ? 'border-violet-300 text-violet-800' : ''
                }`}
              >
                <SelectValue placeholder="Any money state" />
              </SelectTrigger>
              <SelectContent>
                {MONEY_LENSES.map(l => (
                  <SelectItem key={l.key} value={l.key}>{l.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>

            {/* Flag feature retired 2026-09-28 — the brand can no longer
                raise a flag, so this toggle has nothing left to filter by.
                Commented out, not deleted, in case it comes back.
                Flagged videos, as a toggle rather than a tab — like the money
                lens, a flagged hire can be in ANY bucket (still delivering,
                awaiting payout, settled), so it has to filter across the tabs
                instead of being one. Off by default: flags are the exception,
                and the tab counts should keep describing everything. */}
            {/* <Button
              type="button"
              variant="outline"
              size="sm"
              aria-pressed={flaggedOnly}
              title="Show only hires with a brand-flagged video we haven't answered, or an admin-flagged video"
              onClick={() => handleFlaggedOnlyChange(!flaggedOnly)}
              className={`h-8 ${flaggedOnly ? 'border-red-300 bg-red-50 text-red-800 hover:bg-red-100' : ''}`}
            >
              <Flag className={`mr-1 h-3.5 w-3.5 ${flaggedOnly ? 'text-red-600' : ''}`} fill={flaggedOnly ? 'currentColor' : 'none'} />
              Flagged
            </Button> */}

            {/* Sample problems, same overlapping-lens treatment as Flagged
                above — a creator's reported sample issue can sit on a hire in
                any bucket, so it filters across the tabs rather than being
                one of them. */}
            <Button
              type="button"
              variant="outline"
              size="sm"
              aria-pressed={sampleIssueOnly}
              title="Show only hires where the creator reported a problem with their sample"
              onClick={() => handleSampleIssueOnlyChange(!sampleIssueOnly)}
              className={`h-8 ${sampleIssueOnly ? 'border-amber-300 bg-amber-50 text-amber-800 hover:bg-amber-100' : ''}`}
            >
              <PackageX className={`mr-1 h-3.5 w-3.5 ${sampleIssueOnly ? 'text-amber-600' : ''}`} />
              Sample issue
            </Button>

            <Select value={brandFilter} onValueChange={handleBrandChange}>
              <SelectTrigger className="h-8 text-sm w-full sm:w-48">
                <SelectValue placeholder="All brands" />
              </SelectTrigger>
              {/* Was previously side="bottom" + avoidCollisions={false} + a fixed
                  max-h-72, to stop Radix's collision-avoidance from flipping this
                  upward with 50+ brands. That also disabled HORIZONTAL collision
                  avoidance, letting the panel run off the right edge of the
                  viewport when the trigger sits near it. The shared SelectContent
                  (@/components/ui/select) now sizes itself dynamically off
                  --radix-select-content-available-height with its own scroll
                  buttons instead of a fixed height, so full collision detection
                  (the default) keeps the panel on-screen on both axes without
                  needing to flip or overflow — no per-usage overrides needed. */}
              <SelectContent>
                <SelectItem value="all">All brands</SelectItem>
                {brandOptions.map(b => (
                  <SelectItem key={b.id} value={b.id}>{b.brand_name}</SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Select value={campaignFilter} onValueChange={handleCampaignChange}>
              <SelectTrigger className="h-8 text-sm w-full sm:w-52">
                <SelectValue placeholder="All campaigns" />
              </SelectTrigger>
              {/* Same fix as the Brand select above. */}
              <SelectContent>
                <SelectItem value="all">All campaigns</SelectItem>
                {campaigns.map(c => (
                  <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>

            <div className="relative w-full sm:w-64">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground pointer-events-none" />
              <Input
                placeholder="Search creator, campaign..."
                value={searchInput}
                onChange={e => setSearchInput(e.target.value)}
                className="pl-8 h-8 text-sm"
              />
            </div>

            <Button
              type="button"
              variant="outline"
              size="sm"
              className="h-8"
              disabled={isLoading || sortedRows.length === 0}
              title="Export every hire matching the current filters (ignores pagination)"
              onClick={exportCsv}
            >
              <Download className="mr-1 h-3.5 w-3.5" />
              Export CSV
            </Button>
          </div>
        </div>

        {isLoading ? (
          <div className="p-6 space-y-3">
            {Array.from({ length: 5 }).map((_, i) => <Skeleton key={i} className="h-10 w-full" />)}
          </div>
        ) : totalItems === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 text-muted-foreground text-sm gap-2">
            <Video className="h-10 w-10 opacity-25 mb-2" />
            <p className="font-medium">
              {search.trim() ? 'No results match your search.' : TAB_EMPTY[filterTab]}
            </p>
          </div>
        ) : (
          <>
            {/* 11 columns (10 down from 13, plus Brand and Sample). The four that went away
                were merged into the ones they belong with, not deleted: Deadline
                Ext. into Status, Product Match into Videos, and Tx ID + Paid into
                Payment. At 13 the table technically fit, but width was spread so
                thin that the Creator column collapsed to about 75px and broke
                names and emails mid-word — the columns were all present and none
                of them readable. Merging is what buys the width back; see
                ui/table.tsx. */}
            <Table>
                <TableHeader>
                  <TableRow>
                    <SortHead field="creator_name"  label="Creator"  sortField={sortField} sortDir={sortDir} onSort={handleSort} />
                    <TableHead className="whitespace-nowrap">Brand</TableHead>
                    <SortHead field="campaign_name" label="Campaign" sortField={sortField} sortDir={sortDir} onSort={handleSort} />
                    <TableHead className="whitespace-nowrap">Status</TableHead>
                    <TableHead className="whitespace-nowrap">Sample</TableHead>
                    <SortHead field="deadline_at" label="Deadline" sortField={sortField} sortDir={sortDir} onSort={handleSort} />
                    <TableHead className="text-right whitespace-nowrap">Videos</TableHead>
                    <SortHead field="creator_rate_cents" label="Rate" align="right" sortField={sortField} sortDir={sortDir} onSort={handleSort} />
                    {/* Sorted by paid date: this column holds the payout badges AND
                        the transaction record, and "when was it paid" is the only
                        ordering that means anything for that pair. Under a brand
                        refund lens it sorts by refund date instead — the refund
                        badge lives in this column too, and that lens switches the
                        sort to refunded_at, which would otherwise be an active sort
                        with no header showing it or letting ops flip direction. */}
                    <SortHead
                      field={moneyLens.startsWith('brand_') ? 'refunded_at' : 'paid_at'}
                      label="Payment"
                      sortField={sortField} sortDir={sortDir} onSort={handleSort}
                    />
                    <SortHead field="created_at" label="Hired At" sortField={sortField} sortDir={sortDir} onSort={handleSort} />
                    <TableHead />
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {/* Delivery count, refund state, payout state and bucket are all
                      computed once per hire in `decorated` and read here, so the
                      row's badges, its action buttons and the tab count it was
                      filtered by can't disagree about the same hire. */}
                  {pageRows.map(({ hire: h, delivered: submitted, required, match, refund, payout, flags, deletedVideos, adminFlagged }) => {
                    const funding = fundingLabel(h);

                    return (
                      <TableRow key={h.id} className="cursor-pointer hover:bg-muted/50" onClick={() => navigate(`/creator-payments/hires/${h.id}`)}>
                        {/* min-w is the fix for the mid-word breaks: the table lays
                            out on content demand, and with 13 columns this one lost
                            every negotiation, ending up narrow enough that
                            [overflow-wrap:anywhere] chopped "abubaker3@gmail.com"
                            across three lines. A floor plus truncation means a long
                            email costs one clipped line and a tooltip, never five
                            rows of height. */}
                        <TableCell className="min-w-[180px] max-w-[220px]">
                          <TooltipProvider>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <div className="font-medium truncate">{h.roster_creators?.name ?? '—'}</div>
                              </TooltipTrigger>
                              <TooltipContent side="top" className="max-w-xs break-words">
                                {h.roster_creators?.name ?? 'Unnamed creator'}
                                {h.roster_creators?.email && <div className="text-xs opacity-80">{h.roster_creators.email}</div>}
                              </TooltipContent>
                            </Tooltip>
                          </TooltipProvider>
                          {h.roster_creators?.tiktok_username && (
                            <div className="text-xs text-muted-foreground truncate">@{h.roster_creators.tiktok_username}</div>
                          )}
                          {h.roster_creators?.email && (
                            <div className="text-xs text-muted-foreground truncate">{h.roster_creators.email}</div>
                          )}
                        </TableCell>
                        <TableCell className="max-w-[140px]">
                          <TooltipProvider>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <div className="text-sm truncate">{brandNameById.get(h.brand_id) ?? '—'}</div>
                              </TooltipTrigger>
                              <TooltipContent side="top" className="max-w-xs break-words">
                                {brandNameById.get(h.brand_id) ?? h.brand_id}
                              </TooltipContent>
                            </Tooltip>
                          </TooltipProvider>
                        </TableCell>
                        <TableCell className="max-w-[180px]">
                          <TooltipProvider>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <div className="text-sm truncate">{h.cr_campaigns?.name ?? h.campaign_id}</div>
                              </TooltipTrigger>
                              <TooltipContent side="top" className="max-w-xs break-words">
                                {h.cr_campaigns?.name ?? h.campaign_id}
                              </TooltipContent>
                            </Tooltip>
                          </TooltipProvider>
                          {h.cr_campaigns?.product_name && (
                            <div className="text-xs text-muted-foreground truncate">{h.cr_campaigns.product_name}</div>
                          )}
                          {isViralRemake(h.cr_campaigns) && <ViralRemakeBadge className="mt-1 shrink-0 whitespace-nowrap" />}
                        </TableCell>
                        {/* Status + the extension badge under it. The extension was
                            its own column and empty on nearly every row, paying a
                            full column of width for a badge that rarely appears. */}
                        <TableCell>
                          <div className="flex flex-col items-start gap-1">
                            <HireStatusBadge hire={h} delivered={submitted} />
                            <DeadlineExtBadge hire={h} />
                            {/* Brand's rating of this hire, if any — stars only,
                                hover for the reason (see HireRatingStars). */}
                            <HireRatingStars feedback={feedbackByHireId.get(h.id)} />
                          </div>
                        </TableCell>
                        {/* The creator's own "did your sample arrive?" answer
                            (creator app) — separate from any TikTok sample
                            mirror, which this page doesn't otherwise show.
                            Blank until the creator answers. */}
                        <TableCell>
                          {receiptsByHireId[h.id]
                            ? <SampleReceiptBadge receipt={receiptsByHireId[h.id]} live={h.status === 'confirmed' && !h.settled_at} />
                            : <span className="text-muted-foreground text-xs">—</span>}
                        </TableCell>
                        <TableCell><DeadlineCell hire={h} delivered={submitted} required={required} /></TableCell>
                        {/* Delivered count with the product-match split under it —
                            both answer "what actually arrived", and reading them
                            together is how ops decides whether a payout is clean. */}
                        <TableCell className="text-right tabular-nums">
                          <div>
                            <span className="font-medium">{submitted}</span>
                            <span className="text-muted-foreground text-xs"> / {required}</span>
                          </div>
                          {match.total > 0 && (
                            match.unmatched === 0
                              ? <div className="text-[10px] font-medium text-green-600">all matched</div>
                              : <div className="text-[10px] font-medium text-red-500">{match.unmatched} unmatched</div>
                          )}
                          {/* Videos TikTok no longer serves. NOT part of the
                              count above it — that is the whole point of
                              showing it separately. A hire that reads 4 / 5
                              with "1 removed" under it delivered five and lost
                              one, which is a different story from a creator who
                              only ever uploaded four, and it is the reason a
                              completed hire can turn up here reopened. */}
                          {deletedVideos > 0 && (
                            <div className="text-[10px] font-medium text-muted-foreground">
                              {deletedVideos} removed
                            </div>
                          )}
                          {/* Flag feature retired 2026-09-28 — the brand can
                              no longer raise a flag, so this has nothing left
                              to show. Commented out, not deleted, in case it
                              comes back.
                              Brand flags sit with the delivery count: "what
                              arrived" and "what the brand says is wrong with
                              what arrived" are one thought, and a flag column
                              would be empty on almost every row. Silent unless
                              this hire actually has one. */}
                          {/* {(flags.active > 0 || flags.resolved > 0) && (
                            <div className="mt-1 flex justify-end">
                              <HireFlagCountBadge active={flags.active} resolved={flags.resolved} />
                            </div>
                          )}
                          {adminFlagged > 0 && (
                            <div className="mt-1 flex justify-end">
                              <AdminFlagCountBadge count={adminFlagged} />
                            </div>
                          )}
                        </TableCell>
                        <TableCell className="text-right tabular-nums font-medium">
                          <div>${((h.creator_rate_cents ?? 0) / 100).toLocaleString()}</div>
                          {/* How the BRAND paid — the money a refund draws from.
                              Sits under the creator rate rather than in its own
                              column; this table is already at its width budget. */}
                          {funding && (
                            <div className="text-[10px] font-normal text-muted-foreground whitespace-nowrap">{funding}</div>
                          )}
                        </TableCell>
                        {/* One payment story: the badges, then the transaction record
                            underneath. Tx ID and Paid were two more columns that were
                            "—" on every unpaid row — the overwhelming majority. */}
                        <TableCell className="max-w-[150px]">
                          <div className="flex flex-col items-start gap-1">
                            <PaymentBadge status={h.payment_status ?? 'unpaid'} />
                            <PartialPayBadge hire={h} />
                            <RefundBadge hire={h} />
                            {(h.paid_at || h.payment_transaction_id || h.paid_by) && (
                              <div className="text-[10px] text-muted-foreground leading-snug w-full">
                                {h.paid_at && <div className="whitespace-nowrap">{shortDate(h.paid_at)}</div>}
                                {h.payment_transaction_id && (
                                  <div className="font-mono truncate" title={h.payment_transaction_id}>
                                    {h.payment_transaction_id}
                                  </div>
                                )}
                                {h.paid_by && <div className="truncate" title={h.paid_by}>{h.paid_by}</div>}
                              </div>
                            )}
                          </div>
                        </TableCell>
                        <TableCell className="text-sm text-muted-foreground whitespace-nowrap">
                          {h.created_at ? shortDate(h.created_at) : '—'}
                        </TableCell>
                        <TableCell onClick={e => e.stopPropagation()}>
                          {/* Two independent actions on two different parties:
                              Refund credits the BRAND back for work that never
                              arrived, Pay settles the CREATOR payout for work that
                              did. They are NOT mutually exclusive — a hire that
                              delivered 3 of 5 videos owes the brand 2 videos' worth
                              and owes the creator 3, so both can be live at once
                              and both render (stacked). Refund sits on top because
                              it's the first half of that split: a partial payout
                              isn't unlocked until the brand has been made whole. */}
                          <div className="flex flex-col items-start gap-1">
                            {refund.eligible && (
                              <Button
                                size="sm"
                                variant="outline"
                                className="border-violet-300 text-violet-700 hover:bg-violet-50"
                                onClick={() => openRefundModal(h)}
                              >
                                <RotateCcw className="h-3.5 w-3.5 mr-1" />
                                Refund
                              </Button>
                            )}
                            {payout.eligible && (
                              payout.mode === 'partial'
                                // Amber + explicit label: this pays LESS than the
                                // hire's rate, and a plain "Pay" would hide that
                                // behind a button that looks identical to the
                                // full-payout one.
                                ? <Button
                                    size="sm"
                                    variant="outline"
                                    className="border-amber-300 text-amber-800 hover:bg-amber-50 whitespace-nowrap"
                                    onClick={() => openPayModal(h)}
                                  >
                                    Pay {payout.deliveredVideos}/{payout.requiredVideos}
                                  </Button>
                                : <Button size="sm" onClick={() => openPayModal(h)}>Pay</Button>
                            )}
                            {!refund.eligible && !payout.eligible && (
                              refund.inGracePeriod && refund.remainingOwedCents > 0 ? (
                                // Deadline blown but the creator's 48h window is
                                // still open, so the refund isn't ops' to make yet
                                // — it becomes available on its own. Says when,
                                // rather than falling through to a bare "Not
                                // completed" that reads like nothing will happen.
                                <TooltipProvider>
                                  <Tooltip>
                                    <TooltipTrigger asChild>
                                      <span className="text-xs text-amber-700 whitespace-nowrap">
                                        Refund in {graceHoursRemaining(h.deadline_at)}h
                                      </span>
                                    </TooltipTrigger>
                                    <TooltipContent side="left" className="max-w-xs">
                                      {refund.blockedReason}
                                    </TooltipContent>
                                  </Tooltip>
                                </TooltipProvider>
                              ) : (h.payment_status ?? 'unpaid') === 'unpaid' ? (
                                // Short glance-label, with payoutState's prose in
                                // the tooltip — the old bare "Not completed" never
                                // said what ops was waiting for, and on a short
                                // delivery the answer ("refund the brand first") is
                                // an action, not a wait.
                                <TooltipProvider>
                                  <Tooltip>
                                    <TooltipTrigger asChild>
                                      <span className="text-xs text-muted-foreground whitespace-nowrap">
                                        {payout.blockedLabel}
                                      </span>
                                    </TooltipTrigger>
                                    <TooltipContent side="left" className="max-w-xs">
                                      {payout.blockedReason}
                                    </TooltipContent>
                                  </Tooltip>
                                </TooltipProvider>
                              ) : refund.remainingOwedCents <= 0 ? (
                                // Both sides done: the brand has been refunded for
                                // the gap and the creator paid for the rest. Worth a
                                // word rather than an empty cell — the Refundable
                                // tab's server-side filter can't see delivery
                                // counts, so a settled hire can still be listed
                                // there, and a blank action reads as a bug.
                                <span className="text-xs text-muted-foreground">Settled</span>
                              ) : null
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
            </Table>

            <DataPagination
              currentPage={page}
              totalPages={totalPages}
              pageSize={pageSize}
              totalItems={totalItems}
              onPageChange={setPage}
              onPageSizeChange={(s) => { setPageSize(s); setPage(1); }}
              pageSizeOptions={PAGE_SIZE_OPTIONS}
            />
          </>
        )}
      </div>

      {/* Pay modal */}
      <Dialog open={!!payingHire} onOpenChange={(open) => { if (!open) closeModal(); }}>
        <DialogContent className="sm:max-w-md overflow-x-hidden">
          <DialogHeader>
            <DialogTitle>
              {payMethod === 'trolley'
                ? (payingPayout?.mode === 'partial' ? 'Pay Creator (Partial)' : 'Pay Creator')
                : (payingPayout?.mode === 'partial' ? 'Record Partial Payment' : 'Record Payment')}
            </DialogTitle>
            <DialogDescription className="space-y-0.5 pt-1">
              <span className="block font-medium text-foreground">
                {payingHire?.roster_creators?.name ?? '—'}
              </span>
              <span className="block text-xs break-all">
                {payingHire?.cr_campaigns?.name ?? payingHire?.campaign_id}
                {payingHire?.cr_campaigns?.product_name && ` · ${payingHire.cr_campaigns.product_name}`}
              </span>
              <span className="block text-xs">
                Creator rate: <strong>${((payingHire?.creator_rate_cents ?? 0) / 100).toLocaleString()}</strong>
                {' · '}
                Videos submitted: <strong>{payingHire ? videosSubmitted(payingHire) : 0} / {payingHire ? videosRequired(payingHire) : 0}</strong>
              </span>
            </DialogDescription>
          </DialogHeader>

          <TrolleyPayoutMethodToggle value={payMethod} onChange={setPayMethod} disabled={payMutation.isPending} />

          {/* Latest Trolley attempt, read from the live query so Sync/Cancel and
              a just-sent payout show up without reopening the dialog. */}
          {payingTrolley && (
            <TrolleyPayoutStatusCard
              payout={payingTrolley}
              onChanged={() => queryClient.invalidateQueries({ queryKey: ['creator-payments-all'] })}
            />
          )}

          {/* Partial-payout receipt. The full-payout case needs none — the amount
              IS the creator rate already shown above. Here the amount is derived,
              so the derivation has to be on screen: ops is signing off on paying
              less than the hire's rate and should see exactly which videos that
              figure comes from, and that the brand was made whole for the rest. */}
          {payingPayout?.mode === 'partial' && (
            <div className="rounded-md bg-amber-50 border border-amber-200 px-4 py-3 text-sm space-y-2">
              <p className="font-semibold text-amber-900">
                Paying for {payingPayout.deliveredVideos} of {payingPayout.requiredVideos} videos delivered
              </p>
              <div className="text-xs text-amber-800 space-y-1">
                <div className="flex justify-between gap-3">
                  <span>Per video ({usd(payingPayout.fullRateCents)} ÷ {payingPayout.requiredVideos})</span>
                  <span className="tabular-nums">{usd(payingPayout.perVideoCents)}</span>
                </div>
                <div className="flex justify-between gap-3 font-medium">
                  <span>Earned ({payingPayout.deliveredVideos} delivered)</span>
                  <span className="tabular-nums">{usd(payingPayout.earnedCents)}</span>
                </div>
                <div className="flex justify-between gap-3">
                  <span>Withheld ({payingPayout.undeliveredVideos} undelivered)</span>
                  <span className="tabular-nums">−{usd(payingPayout.withheldCents)}</span>
                </div>
              </div>
              {/* Only the partial-refund case reaches this dialog now: a FULL refund
                  writes the job off and payoutState blocks the payout outright, so
                  there is no "this lands on margin" warning to show any more. */}
              {payingPayout.brandCollectedCents > 0 && (
                <p className="text-xs text-amber-700 border-t border-amber-200 pt-2">
                  Brand refunded {usd(payingPayout.brandRefundedCents)} of the {usd(payingPayout.brandCollectedCents)} collected
                  — they&apos;re still paying for the {payingPayout.deliveredVideos} video{payingPayout.deliveredVideos !== 1 ? 's' : ''} that arrived.
                </p>
              )}
              {/* The earned figure counts delivered videos whether or not they
                  matched the product, because that is exactly the set the brand
                  was NOT refunded for — the refund and the payout have to be
                  computed off one delivery count or the two stop reconciling.
                  Unmatched work is a judgement call, so it's surfaced for ops to
                  price down rather than silently deducted here. */}
              {payingHireMatch.unmatched > 0 && (
                <p className="text-xs text-red-700 border-t border-amber-200 pt-2">
                  {payingHireMatch.unmatched} of the {payingPayout.deliveredVideos} delivered
                  video{payingHireMatch.unmatched !== 1 ? 's' : ''} didn&apos;t match this campaign&apos;s
                  product. Lower the amount if that work shouldn&apos;t be paid.
                </p>
              )}
            </div>
          )}

          {showMatchBlock && (
            <div className="rounded-md bg-red-50 border border-red-200 px-4 py-3 text-sm space-y-1">
              <p className="font-semibold text-red-700">Product match incomplete — payout blocked</p>
              <p className="text-red-600">
                <span className="font-medium">{payingHireMatch.matched}</span> video{payingHireMatch.matched !== 1 ? 's' : ''} matched this campaign&apos;s product.
              </p>
              <p className="text-red-600">
                <span className="font-medium">{payingHireMatch.unmatched}</span> video{payingHireMatch.unmatched !== 1 ? 's' : ''} did not match this campaign&apos;s product and cannot be counted toward payout.
              </p>
              <p className="text-red-500 text-xs pt-0.5">All submitted videos must be matched before payment can be recorded.</p>
            </div>
          )}

          {showMatchOk && (
            <div className="rounded-md bg-green-50 border border-green-200 px-4 py-3 text-sm">
              <p className="font-semibold text-green-700">All videos matched</p>
              <p className="text-green-600 text-xs mt-0.5">All {payingHireMatch.matched} submitted video{payingHireMatch.matched !== 1 ? 's' : ''} matched this campaign&apos;s product.</p>
            </div>
          )}

          {payMethod === 'trolley' && payingHire && (
            <TrolleyPayoutSendForm
              key={payingHire.id}
              hireId={payingHire.id}
              onCancel={closeModal}
              onSent={(sent) => {
                queryClient.invalidateQueries({ queryKey: ['creator-payments-all'] });
                // Accepted by Trolley → done here; the row now reads "Trolley
                // sending". Anything else (failed, needs sync) stays open so the
                // status card above can explain it.
                if (sent && (sent.status === 'submitted' || sent.status === 'processed')) closeModal();
              }}
            />
          )}

          {payMethod === 'manual' && (<>
          <div className="space-y-4 py-2">
            <div className="space-y-1.5">
              <Label htmlFor="tx-id">Transaction ID <span className="text-destructive">*</span></Label>
              <Input
                id="tx-id"
                placeholder="e.g. TXN-2026-0001"
                value={txId}
                onChange={e => setTxId(e.target.value)}
              />
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="pay-amount">Amount ($) <span className="text-destructive">*</span></Label>
              <Input
                id="pay-amount"
                type="number"
                min="0"
                step="0.01"
                placeholder="0.00"
                value={amount}
                onChange={e => setAmount(e.target.value)}
              />
              {/* Editable on purpose — ops settles bonuses, agreed adjustments and
                  off-system top-ups here. Overpaying is the only direction that
                  costs money, so it's called out rather than blocked. */}
              {payOverEarned && (
                <p className="text-xs text-amber-700">
                  {usd(payAmountCents - payingPayout.earnedCents)} above the {usd(payingPayout.earnedCents)}{' '}
                  {payingPayout.mode === 'partial'
                    ? `earned for ${payingPayout.deliveredVideos} delivered video${payingPayout.deliveredVideos !== 1 ? 's' : ''}`
                    : 'creator rate for this hire'}.
                </p>
              )}
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="pay-notes">Notes</Label>
              <Textarea
                id="pay-notes"
                placeholder="Optional notes about this payment..."
                value={notes}
                onChange={e => setNotes(e.target.value)}
                rows={2}
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={closeModal} disabled={payMutation.isPending}>
              Cancel
            </Button>
            <Button
              onClick={() => payingHire && payMutation.mutate(payingHire)}
              disabled={payMutation.isPending || !canPay}
            >
              {payMutation.isPending
                ? 'Saving...'
                : payingPayout?.mode === 'partial'
                  // Names the figure being committed: "Mark as Paid" on a partial
                  // payout hides the one number that differs from the hire's rate.
                  ? `Pay ${usd(payAmountCents)}`
                  : 'Mark as Paid'}
            </Button>
          </DialogFooter>
          </>)}
        </DialogContent>
      </Dialog>

      {/* Refund-to-credit modal */}
      {/* The tallest dialog in the app by a wide margin — receipt, refund-type
          picker, video-count input, the list of delivered videos being paid for,
          the credit total and a reason box. It gets a pinned header/footer with
          only the middle scrolling, rather than the base dialog's scroll-the-whole
          thing: on a money action the Refund button must stay reachable without
          scrolling to the end of a variable-length video list.
          !overflow-hidden beats the base's overflow-y-auto deterministically —
          same property, so class order alone wouldn't be reliable. Widened to
          max-w-lg too, which takes real height off the receipt rows. */}
      <Dialog open={!!refundingHire} onOpenChange={(open) => { if (!open) closeRefundModal(); }}>
        <DialogContent className="sm:max-w-lg !overflow-hidden grid-rows-[auto_minmax(0,1fr)_auto]">
          <DialogHeader>
            <DialogTitle>Refund brand as wallet credit</DialogTitle>
            <DialogDescription className="space-y-0.5 pt-1">
              <span className="block font-medium text-foreground">
                {refundingHire?.roster_creators?.name ?? '—'}
              </span>
              <span className="block text-xs break-all">
                {refundingHire?.cr_campaigns?.name ?? refundingHire?.campaign_id}
                {refundingHire?.cr_campaigns?.product_name && ` · ${refundingHire.cr_campaigns.product_name}`}
              </span>
              <span className="block text-xs">
                Deadline: <strong>{refundingHire?.deadline_at ? format(new Date(refundingHire.deadline_at), 'MMM d, yyyy') : '—'}</strong>
                {' · '}
                Delivered: <strong>{refundingState?.deliveredVideos ?? 0} / {refundingHire ? videosRequired(refundingHire) : 0}</strong>
              </span>
            </DialogDescription>
          </DialogHeader>

          {/* The only scrolling region. space-y-4 restores the spacing the
              dialog's own `gap-4` gave these blocks when they were grid rows;
              -mx-6 px-6 puts the scrollbar at the dialog edge while the content
              keeps its padding. */}
          <div className="min-h-0 overflow-y-auto -mx-6 px-6 space-y-4">
          {refundingState && (
            <div className="rounded-md bg-violet-50 border border-violet-200 px-4 py-3 text-sm space-y-1">
              <p className="font-semibold text-violet-800">Credit, not a card refund</p>
              <p className="text-violet-700 text-xs">
                The brand&apos;s card is <strong>not</strong> touched. This adds spendable credit to their
                wallet, applied automatically on their next hire before any card charge.
              </p>
              <div className="pt-1.5 space-y-0.5 text-xs text-violet-800">
                <div className="flex justify-between">
                  <span>Brand paid</span>
                  <span className="tabular-nums font-medium">{usd(refundingState.collectedCents)}</span>
                </div>
                <div className="flex justify-between">
                  <span>{fundingLabel(refundingHire!) ?? 'Funding unknown'}</span>
                  <span />
                </div>
                {refundingState.refundedCents > 0 && (
                  <div className="flex justify-between">
                    <span>Already refunded</span>
                    <span className="tabular-nums font-medium">−{usd(refundingState.refundedCents)}</span>
                  </div>
                )}
                {/* The unsettled entitlement, not collected-minus-refunded: once a
                    refund has landed those diverge, and the gap is the price of
                    videos the brand actually received. */}
                <div className="flex justify-between border-t border-violet-200 pt-1 mt-1 font-semibold">
                  <span>Refundable now</span>
                  <span className="tabular-nums">{usd(refundingState.remainingOwedCents)}</span>
                </div>
                {refundingState.refundableCents > refundingState.remainingOwedCents && (
                  <div className="flex justify-between text-[11px] font-normal text-violet-700/80">
                    <span>
                      Held for {refundingState.deliveredVideos} delivered
                      video{refundingState.deliveredVideos === 1 ? '' : 's'}
                    </span>
                    <span className="tabular-nums">
                      {usd(refundingState.refundableCents - refundingState.remainingOwedCents)}
                    </span>
                  </div>
                )}
              </div>
            </div>
          )}

          <div className="space-y-4 py-2">
            {/* Full vs partial. When the creator delivered nothing there is
                nothing to keep paying for, so partial is hidden entirely rather
                than offered and then rejected. */}
            {refundingState && (
              <div className="space-y-1.5">
                <Label>Refund type <span className="text-destructive">*</span></Label>
                {refundingState.fullOnly ? (
                  <div className="rounded-md border bg-muted/40 px-3 py-2.5 text-sm">
                    <p className="font-medium">Full refund — {usd(refundingState.refundableCents)}</p>
                    <p className="text-xs text-muted-foreground mt-0.5">
                      Nothing was delivered, so the brand gets everything back. A partial refund
                      doesn&apos;t apply here.
                    </p>
                  </div>
                ) : (
                  <div className="grid grid-cols-2 gap-2">
                    {([
                      { key: 'partial' as const, label: 'Partial refund', sub: 'Refund the undelivered videos' },
                      { key: 'full'    as const, label: 'Full refund',    sub: 'Write off the whole hire' },
                    ]).map(opt => (
                      <button
                        key={opt.key}
                        type="button"
                        onClick={() => setRefundMode(opt.key)}
                        className={`text-left rounded-md border px-3 py-2 transition-colors ${
                          refundMode === opt.key
                            ? 'border-violet-400 bg-violet-50 ring-1 ring-violet-300'
                            : 'hover:bg-muted/50'
                        }`}
                      >
                        <span className="block text-sm font-medium">{opt.label}</span>
                        <span className="block text-[11px] text-muted-foreground leading-snug mt-0.5">{opt.sub}</span>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Partial: pick how many undelivered videos to refund. The amount
                is derived from the per-video price the brand actually paid, so
                it can't drift from the video count shown. */}
            {refundingState && refundMode === 'partial' && !refundingState.fullOnly && (
              <div className="space-y-1.5">
                <Label htmlFor="refund-videos">Videos to refund <span className="text-destructive">*</span></Label>
                <div className="flex items-center gap-2">
                  <Input
                    id="refund-videos"
                    type="number"
                    min={1}
                    max={refundingState.refundableVideos}
                    value={refundVideos}
                    onChange={e => {
                      const n = parseInt(e.target.value, 10);
                      setRefundVideos(Number.isFinite(n) ? Math.max(0, Math.min(n, refundingState.refundableVideos)) : 0);
                    }}
                    className="w-24"
                  />
                  {/* Bounded by videos still UNREFUNDED. Offering all the
                      undelivered ones again would re-refund settled work. */}
                  <span className="text-sm text-muted-foreground">
                    of {refundingState.refundableVideos} still refundable
                    {refundingState.refundableVideos !== refundingState.undeliveredVideos && (
                      <span className="text-muted-foreground/70"> ({refundingState.undeliveredVideos} undelivered)</span>
                    )}
                    <span className="text-muted-foreground/70"> · {usd(refundingState.perVideoCents)} per video</span>
                  </span>
                </div>
                {refundVideos < 1 && (
                  <p className="text-xs text-destructive">Refund at least one video, or switch to a full refund.</p>
                )}
              </div>
            )}

            {/* The videos the creator is still being paid for. This is the
                check ops actually needs before refunding: see what arrived
                before deciding what to give back. */}
            {refundingState && refundMode === 'partial' && refundingState.deliveredVideos > 0 && (
              <div className="rounded-md border bg-muted/30 px-3 py-2.5 space-y-2">
                <p className="text-xs font-semibold">
                  Still paying for {videosKeptPaid} delivered video{videosKeptPaid === 1 ? '' : 's'}
                </p>
                <ul className="space-y-1">
                  {(refundingHire?.cr_video_submissions ?? [])
                    .filter(v => v.review_status !== 'rejected' && !v.deleted)
                    .sort((a, b) => new Date(a.submitted_at).getTime() - new Date(b.submitted_at).getTime())
                    .map((v, i) => (
                      <li key={v.id} className="flex items-center gap-2 text-xs">
                        <span className="text-muted-foreground tabular-nums w-4">{i + 1}.</span>
                        {v.tiktok_video_url ? (
                          <a
                            href={v.tiktok_video_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-violet-700 hover:underline truncate max-w-[240px]"
                          >
                            {v.tiktok_video_url.replace(/^https?:\/\/(www\.)?/, '')}
                          </a>
                        ) : (
                          <span className="text-muted-foreground">video {v.id.slice(0, 8)}</span>
                        )}
                        <span className="text-muted-foreground/70 whitespace-nowrap ml-auto">
                          {shortDate(v.submitted_at)}
                        </span>
                        {v.product_match === false && (
                          <Badge className="bg-red-100 text-red-700 border border-red-200 text-[9px] px-1 py-0">
                            no match
                          </Badge>
                        )}
                      </li>
                    ))}
                </ul>
              </div>
            )}

            {/* What actually gets credited. Shown as its own line because the
                amount is derived, not typed — ops should confirm the number
                before committing. */}
            {refundingState && (
              <div className="rounded-md bg-violet-600/10 border border-violet-300 px-3 py-2.5 flex items-baseline justify-between">
                <span className="text-sm font-semibold text-violet-900">Credit to wallet</span>
                <span className="text-lg font-bold tabular-nums text-violet-900">
                  {usd(refundAmountCents)}
                </span>
              </div>
            )}

            <div className="space-y-1.5">
              <Label htmlFor="refund-reason">Reason</Label>
              <Textarea
                id="refund-reason"
                placeholder="Shown to the brand and stored on the ledger entry..."
                value={refundReason}
                onChange={e => setRefundReason(e.target.value)}
                rows={2}
              />
              <p className="text-xs text-muted-foreground">
                Leave blank and we&apos;ll write &quot;{refundingHire?.roster_creators?.name ?? 'The creator'} delivered{' '}
                {refundingState?.deliveredVideos ?? 0} of {refundingHire ? videosRequired(refundingHire) : 0} videos by the deadline&quot;.
              </p>
            </div>

            {refundingState && refundMode === 'full' && (
              <div className="rounded-md bg-amber-50 border border-amber-200 px-3 py-2 text-xs text-amber-800">
                Full refund — the campaign budget is freed and the brand gets every dollar back. The hire
                stays listed for both the brand and the creator; only the money moves. The creator payout
                is separate — settle that yourself if they delivered anything.
              </div>
            )}
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={closeRefundModal} disabled={refundMutation.isPending}>
              Cancel
            </Button>
            <Button
              className="bg-violet-600 hover:bg-violet-700"
              onClick={() => refundingHire && refundMutation.mutate({
                hire: refundingHire,
                amountCents: refundAmountCents,
                reason: refundReason.trim(),
                key: refundKey,
              })}
              disabled={refundMutation.isPending || !canRefund}
            >
              {refundMutation.isPending ? 'Refunding...' : 'Refund to credit'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
